import {jsonParseSafe, undefinedIfThrowSync} from '@coolcolduk/util';
import {Button, Dialog, DialogActions, DialogContent, Link, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {z} from 'zod';
import {COOKIE_CONSENT_KEY} from '../../constants';
import {getClarity} from '../../util/clarity/getClarity';
import {privacyTextLastUpdated} from '../../variables/privacy';

export enum CookieConsent {
  ESSENTIAL = 'essential',
  ALL = 'all',
}

const cookieConsentSchema = z.object({
  version: z.literal(privacyTextLastUpdated),
  consent: z.nativeEnum(CookieConsent),
});

export default function CookieConsentDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const consentData = localStorage.getItem(COOKIE_CONSENT_KEY);
      const consent = jsonParseSafe<z.infer<typeof cookieConsentSchema>>(consentData || '', (d) =>
        undefinedIfThrowSync(() => cookieConsentSchema.parse(d)),
      );
      if (!consent) {
        setOpen(true);
        return;
      }

      getClarity().consent(consent.consent === CookieConsent.ALL);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({version: privacyTextLastUpdated, consent: CookieConsent.ALL}),
    );
    getClarity().consent(true);
    setOpen(false);
  };

  const handleEssentialOnly = () => {
    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({version: privacyTextLastUpdated, consent: CookieConsent.ESSENTIAL}),
    );
    getClarity().consent(false);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
        },
      }}
      PaperProps={{
        sx: {
          m: 2,
          p: 2,
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: 340,
          borderRadius: 3,
        },
      }}
    >
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          We use cookies to enhance your experience. By using this site, you agree to our{' '}
          <Link href="/privacy" target="_blank" rel="noopener">
            Privacy Policy
          </Link>
          .
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleEssentialOnly} color="secondary" variant="outlined" fullWidth>
          Essential Only
        </Button>
        <Button onClick={handleAcceptAll} color="primary" variant="contained" fullWidth>
          Accept All
        </Button>
      </DialogActions>
    </Dialog>
  );
}
